'use client';

import type { Dispatch, SetStateAction } from 'react';

import { useEffect, useState } from 'react';

import { translateTab } from '@/utils/utils';

import { BiSolidCheckCircle, BiSolidError, BiTestTube } from 'react-icons/bi';
import { RiProfileFill } from 'react-icons/ri';
import type {
  FileLocation,
  FileMetadata,
  FileTests,
} from '../../../../types/api.types';
import type { AnalysisTabs } from '../../../../types/common.types';
import type { HandleToggleChangeProps } from '../../../../types/canvas.types';
import { useTour } from '@reactour/tour';
import { Accordion } from '@/components/Accordion';
import { Column } from '@/components/ui/layout/Column';
import { Text } from '@/components/ui/base/Text';
import { Row } from '@/components/ui/layout/Row';

interface AnalysisAccordionProps {
  title: string;
  variant: 'metadata' | 'tests' | 'detection';
  content?: string | FileMetadata | FileTests;
  setPageIndex?: (index: number) => void;
  setHoverDetection?: Dispatch<SetStateAction<string | undefined>>;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  tabLinks?: AnalysisTabs[];
  hideDetectionCanvas?: boolean;
  locations?: FileLocation;
  handleToggleChange?: HandleToggleChangeProps;
  isGoodDetection?: boolean;
  options?: { mrz?: string; ddoc?: string };
  detectionId?: string;
  hoverDetection?: string | undefined;
  disableHover?: boolean;
  selectedDetectionId?: string;
}

export const AnalysisAccordion = ({
  title,
  variant,
  content,
  setHoverDetection,
  onMouseEnter,
  onMouseLeave,
  tabLinks,
  hideDetectionCanvas,
  locations,
  handleToggleChange,
  isGoodDetection,
  options,
  detectionId,
  hoverDetection,
  disableHover = false,
  selectedDetectionId,
}: AnalysisAccordionProps) => {
  const [accordionOpen, setAccordionOpen] = useState(false);

  const { currentStep } = useTour();

  const handleToggle = () => {
    setAccordionOpen((prev) => !prev);
    if (locations !== undefined && !accordionOpen && setHoverDetection) {
      setHoverDetection(detectionId);
    }
  };

  const getDetectionStyles = () => {
    if (
      detectionId &&
      (detectionId === hoverDetection || detectionId === selectedDetectionId) &&
      !hideDetectionCanvas &&
      !disableHover
    ) {
      return 'bg-neutralBG2 dark:bg-opacity-15 rounded-md';
    }
    return 'rounded-t-md  hover:dark:bg-opacity-5 rounded-md';
  };

  // Tour utils
  useEffect(() => {
    if (currentStep === 9 && variant === 'metadata') {
      setAccordionOpen(false);
    }
    if (currentStep === 10 && variant === 'tests') {
      setAccordionOpen(false);
    }
  }, [currentStep, variant]);

  return (
    <Accordion
      header={
        <Row>
          {variant === 'detection' && (
            <span
              className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-sm ${
                isGoodDetection
                  ? 'bg-successBG dark:bg-successBG-dark'
                  : 'bg-errorBG dark:bg-errorBG-dark'
              }`}
            >
              {isGoodDetection ? (
                <BiSolidCheckCircle className="text-2xl text-[#22a322] dark:text-successText-dark" />
              ) : (
                <BiSolidError className="text-2xl text-[#ec3737] dark:text-errorText-dark" />
              )}
            </span>
          )}
          {variant === 'metadata' && (
            <RiProfileFill className="fill-neutralText text-xl dark:fill-neutralText-dark" />
          )}
          {variant === 'tests' && (
            <BiTestTube className="fill-neutralText text-xl dark:fill-neutralText-dark" />
          )}
          <Text weight="normal">{title}</Text>
        </Row>
      }
      isOpen={accordionOpen}
      onToggle={handleToggle}
      onMouseEnter={onMouseEnter}
      disableHover={disableHover}
      onMouseLeave={onMouseLeave}
      chevronPosition="right"
      bg={variant === 'detection' ? 'transparent' : 'neutralBG2'}
      className={`w-full ${variant === 'detection' ? getDetectionStyles() : ''} ${
        disableHover ? 'hover:bg-transparent' : ''
      }`}
    >
      <div className="overflow-hidden text-sm">
        {variant === 'metadata' && (
          <div className="mt-2 flex flex-col justify-between text-neutralText dark:text-neutralBG">
            {!!content &&
              Object.entries((content as FileMetadata) || {})?.map(
                ([key, value], index) => (
                  <div
                    key={index}
                    className="flex items-center border-b border-neutralBG-dark border-opacity-30 py-2 text-neutralText dark:border-[white] dark:border-opacity-30 dark:text-neutralBG"
                  >
                    <div className="flex flex-col">
                      <span className="text-sm font-medium">{key}</span>
                      <span className="mt-0.5 text-sm opacity-70">{value}</span>
                    </div>
                  </div>
                )
              )}
          </div>
        )}

        {variant === 'tests' && (
          <div className="mt-4 flex flex-col justify-between gap-x-4 space-y-3">
            {Object.entries(content as FileTests).map(([key, value], index) => (
              <div
                key={index}
                className="flex items-center space-x-2 border-b border-neutralBG-dark border-opacity-30 pb-2 text-neutralText dark:border-[white] dark:border-opacity-30 dark:text-neutralBG"
              >
                <span
                  className={`flex h-2 w-2 items-center justify-center rounded-full ${
                    value
                      ? 'bg-[#22a322] dark:bg-successText'
                      : 'bg-[#ec3737] dark:bg-errorText'
                  }`}
                ></span>
                <span>{key}</span>
              </div>
            ))}
          </div>
        )}

        {variant === 'detection' && (
          <Column className={'px-2'} onMouseEnter={onMouseEnter} align="start">
            <Text>{content as string}</Text>
            {tabLinks && tabLinks.length > 0 && (
              <Row>
                {tabLinks.map((tabLink) => (
                  <span
                    className="inline-flex flex-nowrap rounded-sm bg-[#bbc0e6] px-2 font-medium uppercase text-neutralText shadow-sm hover:cursor-pointer hover:underline dark:bg-[#3b53c9] dark:text-neutralBG"
                    key={tabLink}
                    onClick={() => handleToggleChange?.(tabLink, options)}
                  >
                    {translateTab(tabLink)}
                  </span>
                ))}
              </Row>
            )}
          </Column>
        )}
      </div>
    </Accordion>
  );
};
